import React, { useRef } from 'react';
import { Bold, Italic, Underline, List, Type, Palette } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  placeholder?: string;
  rows?: number;
}

export default function RichTextEditor({
  value,
  onChange,
  label,
  error,
  placeholder,
  rows = 4
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleColorClick = () => {
    const color = prompt('Enter color (e.g. #000000 or red):');
    if (color) {
      handleFormat('foreColor', color);
    }
  };

  const formatButtons = [
    { icon: Bold, command: 'bold', title: 'Bold (Ctrl+B)' },
    { icon: Italic, command: 'italic', title: 'Italic (Ctrl+I)' },
    { icon: Underline, command: 'underline', title: 'Underline (Ctrl+U)' },
    { icon: List, command: 'insertUnorderedList', title: 'Bullet List' },
    { icon: Type, command: 'insertOrderedList', title: 'Numbered List' },
    { icon: Palette, command: 'color', title: 'Text Color', onClick: handleColorClick }
  ];

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
        <div className="flex flex-wrap gap-1 p-2 border-b bg-gray-50">
          {formatButtons.map((button, index) => (
            <button
              key={index}
              type="button"
              onClick={() => button.onClick ? button.onClick() : handleFormat(button.command)}
              className="p-1.5 hover:bg-gray-200 rounded"
              title={button.title}
            >
              <button.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <div
          ref={editorRef}
          contentEditable
          dangerouslySetInnerHTML={{ __html: value }}
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          className="p-3 min-h-[100px] focus:outline-none"
          style={{ minHeight: `${rows * 1.5}rem` }}
          placeholder={placeholder}
        />
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}